import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Drawer, FormControlLabel, IconButton, InputAdornment,
  MenuItem, Snackbar, Switch, TextField, Tooltip
} from "@mui/material";
import {
  Add as AddIcon, ArchiveOutlined as ArchiveIcon, Close as CloseIcon,
  CodeOutlined as FieldIcon, ContentCopyOutlined as CopyIcon,
  DesktopWindowsOutlined as DesktopIcon, EditOutlined as EditIcon,
  EmailOutlined as EmailIcon, FormatBold as BoldIcon, FormatItalic as ItalicIcon,
  FormatListBulleted as ListIcon, PhoneIphoneOutlined as MobileIcon,
  PreviewOutlined as PreviewIcon, Search as SearchIcon, SendOutlined as SendIcon
} from "@mui/icons-material";
import {
  PortfolioArchiveEmailTemplate,
  PortfolioCreateEmailTemplate,
  PortfolioDuplicateEmailTemplate,
  PortfolioListEmailTemplates,
  PortfolioPublishEmailTemplate,
  PortfolioSendTestEmailTemplate,
  PortfolioUpdateEmailTemplate
} from "../../util/API/portfolionew";
import "./EmailTemplateManager.scss";

const EVENTS = [
  { key: "unit_transfer.finalized", label: "Unit transfer finalized", category: "Unit transfer", description: "Notify sellers and buyers after a unit transfer is finalized." },
  { key: "distribution.finalized", label: "Distribution finalized", category: "Distribution", description: "Notify allocated investors after a distribution is finalized." },
  { key: "distribution.corrected", label: "Distribution corrected", category: "Distribution", description: "Explain a replacement distribution and its updated values." },
  { key: "distribution.reversed", label: "Distribution reversed", category: "Distribution", description: "Notify affected investors that a finalized distribution was reversed." },
  { key: "application.approved", label: "Application approved", category: "Application", description: "Confirm that an investment application was approved." },
  { key: "application.rejected", label: "Application rejected", category: "Application", description: "Communicate an application decision and next steps." },
  { key: "document.uploaded", label: "New document uploaded", category: "Document", description: "Tell an investor that a document is available in the portal." },
  { key: "payout_instruction.approved", label: "Payout instruction approved", category: "Payout", description: "Confirm approval of an investor's payout instruction." },
  { key: "payout_instruction.rejected", label: "Payout instruction rejected", category: "Payout", description: "Request changes to an investor's payout instruction." }
];

const FIELD_GROUPS = [
  {
    label: "Recipient",
    fields: [
      ["Recipient first name", "{{recipient.first_name}}"],
      ["Recipient full name", "{{recipient.full_name}}"],
      ["Entity name", "{{recipient.entity_name}}"]
    ]
  },
  {
    label: "Fund",
    fields: [
      ["Fund name", "{{fund.name}}"],
      ["Fund currency", "{{fund.currency}}"],
      ["Fund manager", "{{fund.manager_name}}"]
    ]
  },
  {
    label: "Unit transfer",
    fields: [
      ["Transfer reference", "{{unit_transfer.reference}}"],
      ["Effective date", "{{unit_transfer.effective_date | date}}"],
      ["Participant side", "{{unit_transfer.side}}"],
      ["Transferred units", "{{unit_transfer.units | decimal:4}}"],
      ["Price per unit", "{{unit_transfer.price_per_unit | money}}"],
      ["Consideration amount", "{{unit_transfer.consideration_amount | money}}"],
      ["Units before transfer", "{{unit_transfer.units_before | decimal:4}}"],
      ["Units after transfer", "{{unit_transfer.units_after | decimal:4}}"],
      ["Unit class", "{{unit_transfer.unit_class_name}}"]
    ]
  },
  {
    label: "Distribution",
    fields: [
      ["Distribution ID", "{{distribution.reference}}"],
      ["Total amount", "{{distribution.total_amount | money}}"],
      ["Investor amount", "{{distribution.investor_amount | money}}"],
      ["Record date", "{{distribution.record_date | date}}"],
      ["DPI", "{{distribution.dpi | decimal:4}}"],
      ["TVPI", "{{distribution.tvpi | multiple:1}}"]
    ]
  },
  {
    label: "Application and document",
    fields: [
      ["Application ID", "{{application.reference}}"],
      ["Application amount", "{{application.amount | money}}"],
      ["Decision date", "{{application.decision_date | date}}"],
      ["Document name", "{{document.name}}"],
      ["Document type", "{{document.type}}"],
      ["Secure portal link", "{{portal.action_url}}"]
    ]
  },
  {
    label: "Distribution highlights",
    fields: [
      ["Average sale price", "{{custom.average_sale_price}}"],
      ["Tokens remaining", "{{custom.tokens_remaining_percent}}"],
      ["Treasury balance", "{{custom.treasury_balance}}"],
      ["Call date and time", "{{custom.call_datetime}}"]
    ]
  }
];

const SAMPLE_VALUES = {
  "recipient.first_name": "Alex",
  "recipient.full_name": "Alex Morgan",
  "recipient.entity_name": "Morgan Capital Ltd.",
  "fund.name": "Naoris SPV I",
  "fund.currency": "USDT",
  "fund.manager_name": "Jan",
  "unit_transfer.reference": "TRF-20261013-A1B2C3D4",
  "unit_transfer.effective_date": "October 13, 2026",
  "unit_transfer.side": "Buyer",
  "unit_transfer.units": "120.00000000",
  "unit_transfer.price_per_unit": "$2.00",
  "unit_transfer.consideration_amount": "$240.00",
  "unit_transfer.units_before": "70.00000000",
  "unit_transfer.units_after": "190.00000000",
  "unit_transfer.unit_class_name": "Default class",
  "distribution.reference": "DIST-24",
  "distribution.total_amount": "$122,736.21",
  "distribution.investor_amount": "$5,243.42",
  "distribution.record_date": "September 23, 2026",
  "distribution.dpi": "0.4251",
  "distribution.tvpi": "5.3X",
  "application.reference": "APP-1042",
  "application.amount": "$25,000.00",
  "application.decision_date": "September 29, 2026",
  "document.name": "Q3 Investor Statement.pdf",
  "document.type": "Investor statement",
  "portal.action_url": "https://portal.example.com/portfolio",
  "custom.average_sale_price": "$0.1184 per token",
  "custom.tokens_remaining_percent": "91%",
  "custom.treasury_balance": "$10,000 USDT",
  "custom.call_datetime": "Thursday, October 8 at 12:30 PM EDT"
};
const ALLOWED_FIELD_KEYS = new Set(Object.keys(SAMPLE_VALUES));

const blankTemplate = () => ({
  id: `local-${Date.now()}`,
  name: "",
  eventKey: "distribution.finalized",
  scope: "global",
  fundId: "",
  sendMode: "manual",
  enabled: true,
  status: "draft",
  version: 1,
  subject: "",
  preheader: "",
  bodyHtml: "<p>Hi {{recipient.first_name}},</p><p>Start writing your message here.</p>",
  updatedAt: "Not saved",
  description: ""
});

const mapTemplate = template => ({
  id: template.id,
  name: template.name || "",
  description: template.description || "",
  eventKey: template.event_key,
  scope: template.scope || "global",
  fundId: template.fund_id || "",
  sendMode: template.send_mode || "manual",
  enabled: Boolean(template.is_enabled),
  status: template.version_status === "draft" ? "draft" : template.status || "draft",
  version: template.version_number || 1,
  subject: template.subject_template || "",
  preheader: template.preheader_template || "",
  bodyHtml: template.body_html || "",
  updatedAt: template.updated_at || ""
});

const apiError = error => error?.response?.data?.message || error?.message || "The request could not be completed.";

export function renderTokens(value = "") {
  return value.replace(/{{\s*([^}|]+?)(?:\s*\|\s*([^}]+?))?\s*}}/g, (_, key, formatter = "") => {
    const field = key.trim();
    const sample = SAMPLE_VALUES[field];
    if (sample === undefined) return `Missing: ${field}`;
    const decimal = formatter.trim().match(/^decimal:(\d+)$/);
    if (decimal && Number.isFinite(Number(sample))) {
      return Number(sample).toFixed(Number(decimal[1]));
    }
    return sample;
  });
}

function safePreviewHtml(value) {
  if (typeof window === "undefined") return value;
  const documentNode = new DOMParser().parseFromString(value || "", "text/html");
  documentNode.querySelectorAll("script,style,iframe,object,embed,form").forEach(node => node.remove());
  documentNode.querySelectorAll("*").forEach(node => {
    [...node.attributes].forEach(attribute => {
      if (attribute.name.toLowerCase().startsWith("on")) node.removeAttribute(attribute.name);
      if (["href", "src"].includes(attribute.name.toLowerCase()) && /^javascript:/i.test(attribute.value.trim())) node.removeAttribute(attribute.name);
    });
  });
  return documentNode.body.innerHTML;
}

function eventFor(key) {
  return EVENTS.find(event => event.key === key) || EVENTS[0];
}

function TemplateStatus({ status }) {
  return <span className={`yp-email-status ${status}`}>{status === "active" ? "Active" : status === "archived" ? "Archived" : "Draft"}</span>;
}

export default function EmailTemplateManager({ funds = [], actorId }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editor, setEditor] = useState(null);
  const [previewMode, setPreviewMode] = useState("desktop");
  const [fieldSearch, setFieldSearch] = useState("");
  const [insertTarget, setInsertTarget] = useState("body");
  const [notice, setNotice] = useState("");
  const [bodySeed, setBodySeed] = useState({ id: "", html: "" });
  const bodyRef = useRef(null);

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await PortfolioListEmailTemplates();
      setTemplates((response.data || []).map(mapTemplate));
    } catch (error) {
      setNotice(apiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(); }, [loadTemplates]);

  const setBodyEditorRef = useCallback(node => {
    bodyRef.current = node;
    if (node && node.innerHTML !== bodySeed.html) {
      node.innerHTML = bodySeed.html;
    }
  }, [bodySeed]);

  const visibleTemplates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return templates.filter(template => {
      const event = eventFor(template.eventKey);
      return (statusFilter === "all" || template.status === statusFilter)
        && (!term || [template.name, event.label, event.category, template.subject].join(" ").toLowerCase().includes(term));
    });
  }, [search, statusFilter, templates]);

  const missingEvents = EVENTS.filter(event => !templates.some(template => template.eventKey === event.key && template.status !== "archived"));
  const updateEditor = (field, value) => setEditor(current => ({ ...current, [field]: value }));

  const openEditor = template => {
    const nextEditor = { ...(template || blankTemplate()) };
    setEditor(nextEditor);
    setBodySeed({ id: nextEditor.id, html: nextEditor.bodyHtml || "" });
    setPreviewMode("desktop");
    setFieldSearch("");
    setInsertTarget("body");
  };

  const saveEditor = async status => {
    if (!editor.name.trim() || !editor.subject.trim() || !editor.bodyHtml.replace(/<[^>]+>/g, "").trim()) {
      setNotice("Template name, subject, and message body are required.");
      return;
    }
    if (editor.scope === "fund" && !editor.fundId) {
      setNotice("Choose a fund for a fund-specific template.");
      return;
    }
    const unknownFields = [...`${editor.subject} ${editor.preheader} ${editor.bodyHtml}`.matchAll(/{{\s*([^}|]+?)(?:\s*\|\s*[^}]+)?\s*}}/g)]
      .map(match => match[1].trim())
      .filter(key => !ALLOWED_FIELD_KEYS.has(key));
    if (unknownFields.length) {
      setNotice(`Unknown calculated field: ${unknownFields[0]}. Choose fields from the approved library.`);
      return;
    }
    if (!actorId) {
      setNotice("Your administrator user ID is unavailable. Please sign in again.");
      return;
    }
    const payload = {
      name: editor.name,
      description: editor.description || "",
      event_key: editor.eventKey,
      scope: editor.scope,
      fund_id: editor.scope === "fund" ? Number(editor.fundId) : null,
      send_mode: editor.sendMode,
      is_enabled: editor.enabled,
      subject_template: editor.subject,
      preheader_template: editor.preheader,
      body_html: editor.bodyHtml,
      body_editor_json: null,
      actor_id: Number(actorId)
    };
    setSaving(true);
    try {
      const existing = templates.some(item => item.id === editor.id);
      const savedResponse = existing
        ? await PortfolioUpdateEmailTemplate(editor.id, payload)
        : await PortfolioCreateEmailTemplate(payload);
      let saved = savedResponse.data;
      if (status === "active") saved = (await PortfolioPublishEmailTemplate(saved.id, Number(actorId))).data;
      setTemplates(current => {
        const mapped = mapTemplate(saved);
        return current.some(item => item.id === mapped.id)
          ? current.map(item => item.id === mapped.id ? mapped : item)
          : [mapped, ...current];
      });
      if (status === "active") await loadTemplates();
      setEditor(null);
      setNotice(status === "active" ? "Template published successfully." : "Draft saved successfully.");
    } catch (error) {
      setNotice(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!editor.name.trim() || !editor.subject.trim() || !editor.bodyHtml.replace(/<[^>]+>/g, "").trim()) {
      setNotice("Template name, subject, and message body are required before sending a test.");
      return;
    }
    if (editor.scope === "fund" && !editor.fundId) {
      setNotice("Choose a fund before sending a test.");
      return;
    }
    if (!actorId) {
      setNotice("Your administrator user ID is unavailable. Please sign in again.");
      return;
    }

    setSendingTest(true);
    try {
      const response = await PortfolioSendTestEmailTemplate({
        name: editor.name,
        description: editor.description || "",
        event_key: editor.eventKey,
        scope: editor.scope,
        fund_id: editor.scope === "fund" ? Number(editor.fundId) : null,
        send_mode: editor.sendMode,
        is_enabled: editor.enabled,
        subject_template: editor.subject,
        preheader_template: editor.preheader,
        body_html: editor.bodyHtml,
        body_editor_json: null,
        actor_id: Number(actorId)
      });
      const address = response?.data?.recipient_email;
      setNotice(address ? `Test email sent to ${address}.` : "Test email sent successfully.");
    } catch (error) {
      setNotice(apiError(error));
    } finally {
      setSendingTest(false);
    }
  };

  const duplicateTemplate = async template => {
    if (!actorId) return setNotice("Your administrator user ID is unavailable. Please sign in again.");
    try {
      const response = await PortfolioDuplicateEmailTemplate(template.id, Number(actorId));
      setTemplates(current => [mapTemplate(response.data), ...current]);
      setNotice("Template duplicated as a draft.");
    } catch (error) { setNotice(apiError(error)); }
  };

  const publishTemplate = async template => {
    if (!actorId) return setNotice("Your administrator user ID is unavailable. Please sign in again.");
    setSaving(true);
    try {
      const response = await PortfolioPublishEmailTemplate(template.id, Number(actorId));
      const published = mapTemplate(response.data);
      setTemplates(current => current.map(item => item.id === published.id ? published : item));
      await loadTemplates();
      setNotice("Template published successfully.");
    } catch (error) {
      setNotice(apiError(error));
    } finally {
      setSaving(false);
    }
  };

  const archiveTemplate = async template => {
    if (!actorId) return setNotice("Your administrator user ID is unavailable. Please sign in again.");
    try {
      const response = await PortfolioArchiveEmailTemplate(template.id, Number(actorId));
      const archived = mapTemplate(response.data);
      setTemplates(current => current.map(item => item.id === archived.id ? archived : item));
      setNotice("Template archived.");
    } catch (error) { setNotice(apiError(error)); }
  };

  const insertToken = token => {
    if (!editor) return;
    if (insertTarget === "subject" || insertTarget === "preheader") {
      const currentValue = editor[insertTarget] || "";
      updateEditor(insertTarget, `${currentValue}${currentValue && !currentValue.endsWith(" ") ? " " : ""}${token}`);
      return;
    }
    bodyRef.current?.focus();
    document.execCommand("insertText", false, token);
    updateEditor("bodyHtml", bodyRef.current?.innerHTML || editor.bodyHtml);
  };

  const formatBody = command => {
    bodyRef.current?.focus();
    document.execCommand(command, false);
    updateEditor("bodyHtml", bodyRef.current?.innerHTML || editor.bodyHtml);
  };

  const filteredFieldGroups = FIELD_GROUPS.map(group => ({
    ...group,
    fields: group.fields.filter(([label, token]) => `${label} ${token}`.toLowerCase().includes(fieldSearch.trim().toLowerCase()))
  })).filter(group => group.fields.length);

  return <section className="yp-panel yp-email-template-panel">
    <div className="yp-email-template-heading">
      <div>
        <span className="yp-eyebrow">Investor communications</span>
        <h2>Email templates</h2>
        <p>Create reusable messages and insert approved calculated fields for portfolio events.</p>
      </div>
      <Button className="yp-primary" startIcon={<AddIcon />} onClick={() => openEditor(null)}>Create template</Button>
    </div>

    <Alert severity="info" className="yp-email-preview-alert">
      Templates and versions are stored centrally. Use Send test to deliver the current draft with sample data to your administrator email address.
    </Alert>

    <div className="yp-email-summary">
      <article><span>Configured templates</span><strong>{templates.filter(template => template.status !== "archived").length}</strong><small>Across {EVENTS.length} supported events</small></article>
      <article><span>Active templates</span><strong>{templates.filter(template => template.status === "active").length}</strong><small>Available to connected event workflows</small></article>
      <article className={missingEvents.length ? "attention" : ""}><span>Events needing a template</span><strong>{missingEvents.length}</strong><small>{missingEvents.length ? missingEvents.map(event => event.label).slice(0, 2).join(" · ") : "All events covered"}</small></article>
    </div>

    <div className="yp-email-toolbar">
      <TextField size="small" placeholder="Search templates or events" value={search} onChange={event => setSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
      <TextField select size="small" label="Status" value={statusFilter} onChange={event => setStatusFilter(event.target.value)}>
        <MenuItem value="all">All statuses</MenuItem>
        <MenuItem value="draft">Draft</MenuItem>
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="archived">Archived</MenuItem>
      </TextField>
      <span>{visibleTemplates.length} template{visibleTemplates.length === 1 ? "" : "s"}</span>
    </div>

    <div className="yp-email-template-table">
      <div className="yp-email-template-table-head"><span>Template</span><span>Event</span><span>Scope</span><span>Delivery</span><span>Status</span><span>Actions</span></div>
      {visibleTemplates.map(template => {
        const event = eventFor(template.eventKey);
        const fund = funds.find(item => String(item.id) === String(template.fundId));
        return <article key={template.id}>
          <div className="yp-email-template-name"><span><EmailIcon /></span><div><strong>{template.name}</strong><small>{template.subject || "No subject"}</small></div></div>
          <div><strong>{event.label}</strong><small>{event.category}</small></div>
          <div><strong>{template.scope === "fund" ? fund?.name || "Fund-specific" : "Global default"}</strong><small>{template.scope === "fund" ? "Overrides global" : "All funds"}</small></div>
          <div><strong>{template.sendMode === "automatic" ? "Automatic" : "Admin approval"}</strong><small>{template.enabled ? "Enabled" : "Disabled"}</small></div>
          <div><TemplateStatus status={template.status} /><small>{template.updatedAt}</small></div>
          <div className="yp-email-row-actions">
            <Tooltip title="Edit template"><IconButton onClick={() => openEditor(template)}><EditIcon /></IconButton></Tooltip>
            {template.status === "draft" && <Tooltip title="Publish saved draft"><span><IconButton disabled={saving} onClick={() => publishTemplate(template)}><SendIcon /></IconButton></span></Tooltip>}
            <Tooltip title="Duplicate template"><IconButton onClick={() => duplicateTemplate(template)}><CopyIcon /></IconButton></Tooltip>
            {template.status !== "archived" && <Tooltip title="Archive template"><IconButton onClick={() => archiveTemplate(template)}><ArchiveIcon /></IconButton></Tooltip>}
          </div>
        </article>;
      })}
      {!visibleTemplates.length && <div className="yp-email-empty"><EmailIcon /><strong>{loading ? "Loading templates" : "No templates found"}</strong><span>{loading ? "Retrieving saved templates." : "Adjust the filters or create a template."}</span></div>}
    </div>

    <Drawer anchor="right" open={Boolean(editor)} onClose={() => setEditor(null)} PaperProps={{ className: "yp-email-editor-drawer" }}>
      {editor && <div className="yp-email-editor">
        <header>
          <div><span className="yp-eyebrow">Communication template</span><h2>{templates.some(item => item.id === editor.id) ? "Edit email template" : "Create email template"}</h2><p>Build the message, select its event, and preview calculated fields with safe sample data.</p></div>
          <IconButton aria-label="Close email template editor" onClick={() => setEditor(null)}><CloseIcon /></IconButton>
        </header>

        <div className="yp-email-editor-layout">
          <main>
            <section className="yp-email-form-card">
              <div className="yp-email-section-heading"><span>01</span><div><strong>Template setup</strong><p>Choose when and where this template will be used.</p></div></div>
              <div className="yp-email-form-grid">
                <TextField required label="Template name" value={editor.name} onChange={event => updateEditor("name", event.target.value)} />
                <TextField required select label="Event" value={editor.eventKey} onChange={event => updateEditor("eventKey", event.target.value)}>{EVENTS.map(item => <MenuItem key={item.key} value={item.key}>{item.label}</MenuItem>)}</TextField>
                <TextField select label="Scope" value={editor.scope} onChange={event => updateEditor("scope", event.target.value)}><MenuItem value="global">Global default</MenuItem><MenuItem value="fund">Specific fund</MenuItem></TextField>
                {editor.scope === "fund" ? <TextField required select label="Fund" value={editor.fundId} onChange={event => updateEditor("fundId", event.target.value)}><MenuItem value="">Select a fund</MenuItem>{funds.map(fund => <MenuItem key={fund.id} value={fund.id}>{fund.name}</MenuItem>)}</TextField> : <TextField label="Applies to" value="All funds without an override" disabled />}
                <TextField select label="Send mode" value={editor.sendMode} onChange={event => updateEditor("sendMode", event.target.value)}><MenuItem value="manual">Administrator approval</MenuItem><MenuItem value="automatic">Automatic after event</MenuItem></TextField>
                <FormControlLabel control={<Switch checked={editor.enabled} onChange={event => updateEditor("enabled", event.target.checked)} />} label="Enable this event configuration" />
              </div>
              <Alert severity={editor.sendMode === "automatic" ? "warning" : "success"}>{eventFor(editor.eventKey).description} {editor.sendMode === "automatic" ? "Automatic delivery will require backend safeguards and idempotency." : "An administrator will review recipients and content before sending."}</Alert>
            </section>

            <section className="yp-email-form-card">
              <div className="yp-email-section-heading"><span>02</span><div><strong>Message</strong><p>Write the subject and complete email body.</p></div></div>
              <TextField required fullWidth label="Subject" value={editor.subject} onFocus={() => setInsertTarget("subject")} onChange={event => updateEditor("subject", event.target.value)} />
              <TextField fullWidth label="Preview text (optional)" value={editor.preheader} onFocus={() => setInsertTarget("preheader")} onChange={event => updateEditor("preheader", event.target.value)} helperText="Displayed beside the subject by supported email clients." />
              <div className="yp-email-composer">
                <div className="yp-email-format-toolbar">
                  <Tooltip title="Bold"><IconButton size="small" onClick={() => formatBody("bold")}><BoldIcon /></IconButton></Tooltip>
                  <Tooltip title="Italic"><IconButton size="small" onClick={() => formatBody("italic")}><ItalicIcon /></IconButton></Tooltip>
                  <Tooltip title="Bulleted list"><IconButton size="small" onClick={() => formatBody("insertUnorderedList")}><ListIcon /></IconButton></Tooltip>
                  <span>Use the field library to add calculated values.</span>
                </div>
                <div ref={setBodyEditorRef} className="yp-email-content-editor" contentEditable suppressContentEditableWarning onFocus={() => setInsertTarget("body")} onInput={event => updateEditor("bodyHtml", event.currentTarget.innerHTML)} />
              </div>
            </section>
          </main>

          <aside className="yp-email-fields">
            <div><span className="yp-eyebrow">Calculated fields</span><strong>Insert dynamic content</strong><p>Fields are resolved by the backend when a communication is prepared.</p></div>
            <TextField select size="small" label="Insert into" value={insertTarget} onChange={event => setInsertTarget(event.target.value)}><MenuItem value="subject">Subject</MenuItem><MenuItem value="preheader">Preview text</MenuItem><MenuItem value="body">Message body</MenuItem></TextField>
            <TextField size="small" placeholder="Search fields" value={fieldSearch} onChange={event => setFieldSearch(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
            <div className="yp-email-field-groups">{filteredFieldGroups.map(group => <section key={group.label}><strong>{group.label}</strong>{group.fields.map(([label, token]) => <button type="button" key={token} onMouseDown={event => { event.preventDefault(); insertToken(token); }}><FieldIcon /><span>{label}<small>{token}</small></span><AddIcon /></button>)}</section>)}</div>
          </aside>

          <aside className="yp-email-preview-pane">
            <div className="yp-email-preview-heading"><div><span className="yp-eyebrow">Live preview</span><strong>Sample recipient</strong></div><div><Tooltip title="Desktop"><IconButton className={previewMode === "desktop" ? "active" : ""} onClick={() => setPreviewMode("desktop")}><DesktopIcon /></IconButton></Tooltip><Tooltip title="Mobile"><IconButton className={previewMode === "mobile" ? "active" : ""} onClick={() => setPreviewMode("mobile")}><MobileIcon /></IconButton></Tooltip></div></div>
            <div className={`yp-email-preview-frame ${previewMode}`}>
              <div className="yp-email-preview-meta"><span>To: Alex Morgan &lt;alex@example.com&gt;</span><strong>{renderTokens(editor.subject) || "Email subject"}</strong>{editor.preheader && <small>{renderTokens(editor.preheader)}</small>}</div>
              <div className="yp-email-preview-brand"><EmailIcon /><strong>PAiY Portfolio</strong></div>
              <div className="yp-email-preview-message" dangerouslySetInnerHTML={{ __html: safePreviewHtml(renderTokens(editor.bodyHtml)) }} />
              <footer>This communication was sent through the PAiY investor portal.</footer>
            </div>
            <Alert severity="info">Preview values are examples only. Actual values will come from the selected event and recipient.</Alert>
          </aside>
        </div>

        <footer className="yp-email-editor-actions">
          <Button onClick={() => setEditor(null)}>Cancel</Button>
          <Tooltip title="Send the current draft with sample data to your administrator email"><span><Button disabled={saving || sendingTest} onClick={sendTestEmail} startIcon={<SendIcon />}>{sendingTest ? "Sending..." : "Send test"}</Button></span></Tooltip>
          <Button variant="outlined" startIcon={<PreviewIcon />} onClick={() => setPreviewMode(current => current === "desktop" ? "mobile" : "desktop")}>Toggle preview</Button>
          <div className="yp-email-save-actions">
            <Button disabled={saving} variant="outlined" onClick={() => saveEditor("draft")}>{saving ? "Saving..." : "Save draft"}</Button>
            <Button disabled={saving} className="yp-primary" startIcon={<SendIcon />} onClick={() => saveEditor("active")}>{saving ? "Publishing..." : "Save & publish"}</Button>
          </div>
        </footer>
      </div>}
    </Drawer>

    <Snackbar open={Boolean(notice)} autoHideDuration={5000} onClose={() => setNotice("")} message={notice} />
  </section>;
}
